import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Filter, Leaf, Droplets, Cpu, ArrowRight, Zap } from 'lucide-react';
import { mockData } from '../mock';
import GoodBloom from '../assets/GoodBloom3.png'

const TechnologiesSection = () => {
  const { technologies } = mockData;

  const iconMap = {
    filter: Filter,
    leaf: Leaf,
    droplets: Droplets,
    cpu: Cpu
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="technologies" className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-teal-100 to-cyan-100 rounded-full blur-3xl opacity-30 -translate-x-48 -translate-y-48"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-amber-100 to-orange-100 rounded-full blur-3xl opacity-30 translate-x-48 translate-y-48"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-in fade-in slide-in-from-bottom-6 duration-800">
          <div className="inline-flex items-center px-4 py-2 bg-teal-100 rounded-full text-teal-700 font-medium mb-4">
            <Zap className="w-4 h-4 mr-2" />
            Innovation Technologique
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {technologies.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {technologies.subtitle}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {technologies.features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <Card key={feature.id} 
                    className={`group hover:shadow-2xl transition-all duration-500 border-0 bg-white/80 backdrop-blur-sm hover:-translate-y-2 animate-in fade-in slide-in-from-bottom-6 duration-800`}
                    style={{ animationDelay: `${index * 150}ms` }}>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Process Visualization */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="animate-in fade-in slide-in-from-left-6 duration-800 delay-400">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Processus de Dépollution Avancé
            </h3>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Nos systèmes autonomes combinent plusieurs technologies de pointe pour une dépollution complète et respectueuse de l'environnement.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4 p-4 bg-teal-50 rounded-xl border border-teal-100">
                <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                <span className="text-gray-800 font-medium">Détection automatique des polluants</span>
              </div>
              <div className="flex items-center space-x-4 p-4 bg-cyan-50 rounded-xl border border-cyan-100">
                <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                <span className="text-gray-800 font-medium">Filtration sélective multi-niveaux</span>
              </div>
              <div className="flex items-center space-x-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                <span className="text-gray-800 font-medium">Traitement biologique des nutriments</span>
              </div>
              <div className="flex items-center space-x-4 p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                <span className="text-gray-800 font-medium">Monitoring et optimisation continue</span>
              </div>
            </div>
          </div>

          <div className="relative animate-in fade-in slide-in-from-right-6 duration-800 delay-600">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-200 to-cyan-200 rounded-3xl transform rotate-3 scale-105 opacity-20"></div>
            <img 
              src={GoodBloom} 
              alt="Processus de dépollution Good-Bloom"
              className="relative w-full h-96 object-cover rounded-3xl shadow-2xl border-4 border-white"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-teal-900/30 to-transparent rounded-3xl"></div>
          </div>
        </div>

        {/* CTA Section
        <div className="text-center animate-in fade-in slide-in-from-bottom-6 duration-800 delay-800">
          <Button 
            onClick={() => scrollToSection('realisations')}
            size="lg"
            className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg group text-lg font-semibold"
          >
            {technologies.ctaText}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Button>
        </div> */}
      </div>
    </section>
  );
};

export default TechnologiesSection;